/* eslint-disable @next/next/no-img-element */
import { ArrowLeft, CheckCircle2 } from 'lucide-react'
import Link from 'next/link'

import { NON_FSDS_DEFAULT_LOGO_URL } from '@/configs/non-fsds-config'
import { TEMPLATE_DEFAULT_URLS } from '@/configs/template-default-urls'

export interface NonFsdsPasswordResetSuccessfulSectionProps {
  title: string
  subtitle: string
  logoUrl: string
  loginButtonText: string
  backLinkText: string
  brandHref?: string
  signInUrl?: string
}

export default function NonFsdsPasswordResetSuccessfulSection({
  title = 'Password Set Successfully!',
  subtitle = 'Your password has been successfully set. You can now log in using your new credentials.',
  logoUrl = NON_FSDS_DEFAULT_LOGO_URL,
  loginButtonText = 'Log In',
  backLinkText = 'Back to log in',
  brandHref = TEMPLATE_DEFAULT_URLS.home,
  signInUrl = TEMPLATE_DEFAULT_URLS.signIn,
}: NonFsdsPasswordResetSuccessfulSectionProps) {
  return (
    <div className="relative flex min-h-svh flex-col items-center justify-center overflow-hidden bg-slate-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="pointer-events-none absolute left-0 top-0 h-full w-full bg-[radial-gradient(circle_at_top_left,_var(--tw-gradient-stops))] from-blue-100 via-transparent to-transparent opacity-40" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
      <div className="pointer-events-none absolute left-10 top-20 h-72 w-72 rounded-full bg-blue-300/10 blur-3xl" />

      <div className="relative z-10 w-full max-w-md rounded-3xl border border-slate-100/50 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:p-12">
        <div className="mb-8 flex justify-center">
          <Link href={brandHref} className="inline-block transition-transform duration-200 hover:scale-105 active:scale-95">
            <img src={logoUrl} alt="Logo" className="h-10 w-auto" />
          </Link>
        </div>

        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="mb-2 rounded-full bg-green-50 p-3">
            <CheckCircle2 className="h-10 w-10 text-green-500" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900">{title}</h3>
          <p className="text-center text-sm text-slate-500">{subtitle}</p>
          <Link
            href={signInUrl}
            className="mt-4 flex w-full items-center justify-center rounded-xl border border-blue-700 bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:bg-blue-700 hover:shadow-blue-600/25 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
          >
            {loginButtonText}
          </Link>
        </div>

        <div className="mt-8 text-center">
          <Link
            href={signInUrl}
            className="inline-flex items-center justify-center rounded-md text-sm font-semibold text-slate-500 transition-colors hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {backLinkText}
          </Link>
        </div>
      </div>
    </div>
  )
}
